import {
  Button,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import React, { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ErrorTypography, FormField } from "../../styled/formComponents";
import { Form, Formik } from "formik";
import { theme } from "../../../styles/themeProvider";
import GoogleLogin from "./GoogleLogin";
import { RegisterButton } from "../../buttons/RegisterButton";
import { TokenForm } from "./TokenForm";
import { UserApi } from "../../../api/userApi";
import { tryCatchHandler } from "../../../utils/tryCatchHandler";
import { MultistepContext, MultistepContextValues } from "../../../types/context";
import { emptyUser } from "../../../types/entities";

interface RecoverValues {
  email: string;
}

export const RecoverPassword = ({
  setHeaderTitle,
  setSuccess,
  setGoogleError,
}) => {
  const { formatMessage } = useIntl();

  const { onChangeLogin } = useContext(
    MultistepContext
  ) as MultistepContextValues;

  const [email, setEmail] = useState("");
  const [emailSended, setEmailSended] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingError, setSendingError] = useState("");

  const initialValues = {
    email: "",
  };

  const recoverSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .required(
        formatMessage({
          id: "error.required",
          defaultMessage: "Campo requerido.",
        })
      )
      .email(
        formatMessage({
          id: "error.email",
          defaultMessage: "Debe ingresar un mail.",
        })
      ),
  });

  const handleForgotPassword = async (values: RecoverValues) => {
    setSendingError("");
    setEmailSended(false);
    setLoading(true);

    const errorMessage = await tryCatchHandler(
      async () => {
        const userApi = new UserApi();
        const response = await userApi.forgotPassword(values.email);

        onChangeLogin({ ...emptyUser, id: response.data.id });
        const title = formatMessage({
          id: "recoverPassword.checkEmail",
          defaultMessage: "Revisa tu bandeja de entrada",
        });
        setHeaderTitle(title);
        setSuccess(
          formatMessage({
            id: "recoverPassword.emailSended",
            defaultMessage: "Email enviado con éxito.",
          })
        );
        setEmailSended(true);
        setEmail(values.email);
      },
      "recoverPassword.error",
      "Ocurrió un error al enviar el email.",
      formatMessage
    );

    if (errorMessage) setSendingError(errorMessage);

    setLoading(false);
  };

  return (
    <Stack
      sx={{ width: { mobile: "90%", xs: "75%", sm: "70%", md: "65%" } }}
      spacing={2}
    >
      <Typography
        sx={{ fontWeight: 400, fontSize: "15px" }}
        alignSelf="flex-start"
      >
        {emailSended ? (
          <>
            <FormattedMessage
              id="recoverPassword.instructions"
              defaultMessage="Se ha enviado un correo con instrucciones a"
            />
            {" " + email}
          </>
        ) : (
          <FormattedMessage
            id="recoverPassword.message"
            defaultMessage="No te preocupes, enviaremos las instrucciones a seguir al email registrado."
          />
        )}
      </Typography>
      {emailSended ? (
        <TokenForm email={email} setSuccess={setSuccess} />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={recoverSchema}
          onSubmit={async (values) => await handleForgotPassword(values)}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form style={{ width: "100%", alignItems: "center" }}>
              <Stack spacing={2}>
                <FormField
                  name="email"
                  label="Email"
                  type="email"
                  autoComplete="current-email"
                  value={values.email}
                  touched={touched.email}
                  error={errors.email}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                />
                {sendingError && (
                  <ErrorTypography>{sendingError}</ErrorTypography>
                )}

                {loading && <LinearProgress />}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{ height: "50px" }}
                  onClick={(event) => {
                    event.preventDefault();
                    handleSubmit();
                  }}
                >
                  <FormattedMessage
                    id="recoverPassword.title"
                    defaultMessage="Recuperar contraseña"
                  />
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      )}
      <Divider
        sx={{
          height: "2px",
          backgroundColor: theme.palette.disabled!.light,
        }}
      />
      <GoogleLogin setError={setGoogleError} />
      <Stack
        direction="row"
        alignSelf="flex-end"
        alignItems="center"
        marginTop={1}
      >
        <RegisterButton />
      </Stack>
    </Stack>
  );
};

export default RecoverPassword;
